/**
 * useSessionCredentials — tracks credential bindings for an agent session.
 *
 * Backed by server-side SQLite (session_credential table) — persists across
 * page refresh, reconnect, and years of inactivity.
 *
 * Under the server-resolved per-turn pipeline (see
 * features/agent/api/credential-resolver.ts), this hook holds **only
 * metadata**: which entries are bound, what env var names the AI
 * expects, what file/label is shown in the popover. No raw values
 * ever live in browser memory — the API resolves them at job submit
 * time straight from the keybook entry. That model means:
 *
 *   - revoking access in the keybook takes effect on the next turn,
 *   - plaintext never rides in HTTP request bodies,
 *   - there's nothing to "auto-restore" after a page refresh — the
 *     server reads the same SQLite rows when the next turn fires.
 */
import { useState, useCallback, useRef, useEffect } from 'react'
import { request } from '@papan/api-client'
import { keybookAvailable } from '@papan/core-ui/ui'
import { usableEnvName } from '@papan/sandbox/env-names'
import { credToEnvMapping, keyFilledMapping } from '../core/cred-env-mapping.js'
import { useTranslation } from '@papan/i18n'
import { CREDENTIAL_DROPS_EVENT, PERMANENT_CREDENTIAL_DROP_REASONS, type CredentialDropsEventDetail } from '../core/credential-drop-notice.js'
import { describeCredentialDrop } from './credential-drop-message.js'

/** Each (credential, reason) is announced ONCE while it keeps dropping (review rounds 1–2): a transient drop (can't
 *  decrypt, a value missing) keeps its row and is reported again on every turn — the user hears it once, not on every
 *  message — and once, whichever of the chat surfaces holding the session sees it first. Forgotten when a turn's report
 *  no longer has it (it resolved, or its row is gone) or when the credential syncs again (bind / re-add), so a later
 *  recurrence is announced again. Sessions kept: a small LRU (page-life hygiene). */
const ANNOUNCED_SESSIONS_MAX = 50
/** One report, applied once — whichever chat surface on the session sees it first. */
const announcedReports = new WeakSet<object>()
const announcedDrops = new Map<string, Set<string>>()
const dropKey = (requestId: string, reason: string) => `${requestId}\u0000${reason}`
/** Applies one turn's report for a session; returns the drops to announce now. */
export function takeNewDrops<T extends { requestId: string; reason: string }>(sessionId: string, drops: readonly T[]): T[] {
  const before = announcedDrops.get(sessionId) ?? new Set<string>()
  announcedDrops.delete(sessionId) // re-inserted last: most recently used
  const now = new Set(drops.map(d => dropKey(d.requestId, d.reason)))
  if (now.size > 0) announcedDrops.set(sessionId, now)
  while (announcedDrops.size > ANNOUNCED_SESSIONS_MAX) announcedDrops.delete(announcedDrops.keys().next().value as string)
  return drops.filter(d => !before.has(dropKey(d.requestId, d.reason)))
}
/** A credential synced again (bound, re-added): whatever was announced for it may be said again. */
function forgetDrops(sessionId: string | undefined, requestId: string): void {
  const seen = sessionId ? announcedDrops.get(sessionId) : undefined
  if (!seen) return
  for (const k of [...seen]) if (k.startsWith(requestId + '\u0000')) seen.delete(k)
}
/** Tests: the page-life memory. */
export function announcedDropSessions(): number { return announcedDrops.size }

/** Credential field type — matches FieldType in features/keybook/core/types.ts */
export type FieldType = 'text' | 'password' | 'textarea' | 'file' | 'prompt' | 'oauth'

export interface CredRequest {
  requestId: string
  file?: string
  /** AI-provided label (kept for new entry naming) */
  label?: string
  /** Keybook entry label (for display when bound) */
  entryLabel?: string
  envMapping: Record<string, string>
  /** Per-field type — used to drive form rendering. Missing for legacy
   *  entries; falls back to a name heuristic in CredentialPrompt. */
  fieldTypes?: Record<string, FieldType>
  entryId?: string
  status: 'pending' | 'active'
  /** #1324 S2: 'kind' once a card the person confirmed bound it (`bind`) — the server then resolves it with the shared
   *  matcher. Never set by a load from the server or an automatic re-send. */
  fieldMatch?: 'kind'
  /** #1441: the EXACT field map the card showed (requested field → stored field name), sent with a card's own bind
   *  only — the server then resolves through it and never re-matches. Never set by a load or an automatic re-send. */
  fieldMap?: Record<string, string>
  /** Service this credential authenticates against — 'github' etc.
   *  Drives provider-specific UI (Authorize/PAT picker for github)
   *  and survives page refresh via the persisted session_credential
   *  row, so the picker shows up the same way for fresh and
   *  restored rows. */
  provider?: string
}

export interface UseSessionCredentialsResult {
  credentials: CredRequest[]
  addPending: (cred: { requestId: string; file?: string; label?: string; envMapping: Record<string, string>; fieldTypes?: Record<string, FieldType>; provider?: string }) => void
  /** Bind a credential to its keybook entry. Values aren't passed in — the
   *  server-side per-turn resolver loads them from the entry at job submit
   *  time. `envMapping`, when given, is WRITTEN (the card's: what the tool told
   *  the model on this run). Returns once the session_credential row exists on
   *  the server; await before firing a follow-up message whose worker run will
   *  resolve against that row. Resolves true once the row is written (a draft: held for flushDraft), false when
   *  that POST failed (never rejects) — I198: the card reports it. */
  bind: (requestId: string, entryId: string, envMapping?: Record<string, string>, fieldMap?: Record<string, string>, fieldTypes?: Record<string, FieldType>) => Promise<boolean>
  /** A credential card was answered: bind THAT call's row (by its tool call id — the one way to find it) with the
   *  card's mapping, or drop it when the keybook save failed (no entry). Resolves bind()'s answer (false when
   *  dropped) — the card awaits it before replying (I198). */
  resolveCard: (callId: string, envMapping: Record<string, string>, entryId?: string, fieldMap?: Record<string, string>, opts?: { oneTimeOnly?: boolean; fieldTypes?: Record<string, FieldType> }) => Promise<boolean>
  /** Add an existing keybook entry as an active session credential, and tell the model its names once the row is
   *  SAVED (a draft: once flushDraft lands it). Env var names get `_2`, `_3`… on collision so the new one doesn't
   *  shadow an existing one. Returns the mapping (post-rename), or null if the entryId is already bound. */
  addFromKeybook: (cred: { entryId: string; entryLabel: string; file?: string; envMapping: Record<string, string> }) => Record<string, string> | null
  /** True if this entryId is already bound to a credential in the session */
  hasEntry: (entryId: string) => boolean
  /** Remove a credential from session + insert system message.
   *  Pass 'keybook-deleted' when the backing keybook entry is gone
   *  (e.g. cross-tab delete) so the system message matches the cause. */
  remove: (requestId: string, reason?: 'user' | 'keybook-deleted' | 'one-time') => void
  clear: () => void
  /** Load credentials from server for the given session (a draft's rows are flushed to it first — flushDraft). */
  load: (sessionId: string, workspaceId: string) => Promise<void>
  /** The draft → real handoff: save the draft's rows to `sessionId`, then post the notes of the keybook-added ones
   *  that were saved. The send path awaits it before the first message; load() runs the same flush (one promise). */
  flushDraft: (sessionId: string, workspaceId: string) => Promise<void>
}

/** The model's note for credentials added from the keybook — posted only after the row is saved. */
export function manualCredentialNote(entryLabel: string, mapping: Record<string, string>): string {
  const envVarList = Object.values(mapping).map(v => `$${v}`).join(', ')
  return `User added credential "${entryLabel}" to the session. Available environment variables: ${envVarList}. Use them directly in bash commands. Do not call cred_to_env for this credential.`
}

function isDraftId(id: string | null | undefined): boolean {
  return !!id && id.startsWith('draft_')
}

/** The server refused the row's env names (400 INVALID_ENV_NAME): it will never exist. Any other failure is a
 *  failed save — the row is kept, best-effort, as before (review round 11: every 4xx used to drop it, and the
 *  cred_to_env effect re-added it → re-POST → the same 4xx → a loop). */
function isRefusedRow(err: unknown): boolean {
  const e = err as { status?: unknown; code?: unknown } | null
  return e?.status === 400 && e?.code === 'INVALID_ENV_NAME'
}

type ToolCallMap = ReadonlyMap<string, { toolName: string; input: Record<string, unknown> }>
type CredToEnvInput = { file?: string; label?: string; provider?: string; mode?: 'auto' | 'update' | 'new_env'; context?: { repo?: string }; fields?: Array<{ name: string; type: FieldType; env?: string }> }

export interface UseSessionCredentialsOptions {
  /** The server REFUSED a credential row's env names (400 INVALID_ENV_NAME): the optimistic row is dropped, and this
   *  tells the user why (review round 8 — it used to be swallowed, leaving a card that looked active). */
  onSyncRefused?: (message: string) => void
  /** The turn's running tool calls: each cred_to_env call gets its pending row here (one copy for every chat
   *  surface — round 11), with THE mapping the worker told the model (core/cred-env-mapping.ts). */
  activeToolCalls?: ToolCallMap
  /** D52: the server dropped one of this session's credentials at the start of a turn (any reason — an env name the
   *  sandbox refuses, a revoked or deleted Keybook entry, …): a short, plain notice naming it and why, one per drop. */
  onDropped?: (message: string) => void
}

export function useSessionCredentials(opts: UseSessionCredentialsOptions = {}): UseSessionCredentialsResult {
  const [credentials, setCredentials] = useState<CredRequest[]>([])
  const onSyncRefusedRef = useRef(opts.onSyncRefused)
  onSyncRefusedRef.current = opts.onSyncRefused
  const onDroppedRef = useRef(opts.onDropped)
  onDroppedRef.current = opts.onDropped
  const { t } = useTranslation('agent')
  const tRef = useRef(t)
  tRef.current = t
  const sessionRef = useRef<{ sessionId: string; workspaceId: string } | null>(null)
  // Mirror state in a ref so load() can flush without adding state to deps.
  const credsRef = useRef<CredRequest[]>([])
  credsRef.current = credentials
  /** Calls whose row the server refused: never re-added (the effect would loop on the same 400). */
  const refusedIds = useRef(new Set<string>())
  /** Notes for keybook-added rows of a draft, posted once flushDraft has saved the row. */
  const draftNotes = useRef(new Map<string, string>())
  const flushing = useRef<{ sessionId: string; done: Promise<void> } | null>(null)

  /** POST one row; true when the server holds it. Never rejects. A refused row (its names) is dropped, remembered
   *  and reported; any other failure keeps it (best-effort). */
  const postRow = useCallback((sessionId: string, workspaceId: string, row: Partial<CredRequest> & { requestId: string }): Promise<boolean> =>
    request(`/api/agent/sessions/${sessionId}/credentials?workspaceId=${workspaceId}`, {
      method: 'POST',
      body: JSON.stringify({
        requestId: row.requestId,
        file: row.file,
        label: row.label,
        envMapping: row.envMapping,
        entryId: row.entryId,
        status: row.status,
        provider: row.provider,
        // #1324 S2: set only by a card's own bind (`bind` below) — the person saw the shared matcher's fills, so the
        // server may resolve the binding the same way. An automatic re-send carries none (syncToServer strips it).
        ...(row.fieldMatch === 'kind' ? { fieldMatch: 'kind' } : {}),
        // #1441: likewise only a card's own bind carries its exact field map
        ...(row.fieldMap ? { fieldMap: row.fieldMap } : {}),
      }),
    }).then(() => true, (err: unknown) => {
      if (isRefusedRow(err)) {
        refusedIds.current.add(row.requestId)
        setCredentials(prev => prev.filter(c => c.requestId !== row.requestId))
        onSyncRefusedRef.current?.(err instanceof Error ? err.message : 'The credential was refused')
      } else {
        console.warn('[session-credentials] saving the credential row failed:', err)
      }
      return false
    }), [])

  /** Save a row (the stored fields, overlaid by `data`) to the current session; resolves false for a draft (the
   *  flush saves it) or a failed save. Never throws. */
  const syncToServer = useCallback((requestId: string, data: Partial<CredRequest>): Promise<boolean> => {
    if (!sessionRef.current || isDraftId(sessionRef.current.sessionId)) return Promise.resolve(false)
    const { sessionId, workspaceId } = sessionRef.current
    const cred = credentials.find(c => c.requestId === requestId) || data as CredRequest
    // a re-send never carries the card's field-matching flag (#1324 S2) — only `bind` passes it, in `data`
    return postRow(sessionId, workspaceId, { ...cred, fieldMatch: undefined, fieldMap: undefined, ...data, requestId })
  }, [credentials, postRow])

  const postNote = (sessionId: string, workspaceId: string, content: string) =>
    request(`/api/agent/sessions/${sessionId}/system-message?workspaceId=${workspaceId}`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    }).catch(() => {})

  const flushDraft = useCallback((sessionId: string, workspaceId: string): Promise<void> => {
    if (flushing.current?.sessionId === sessionId) return flushing.current.done
    const prev = sessionRef.current
    if (prev && !isDraftId(prev.sessionId)) return Promise.resolve() // not a draft: nothing to hand over
    sessionRef.current = { sessionId, workspaceId }
    const rows = credsRef.current
    const done = Promise.all(rows.map(async (c) => {
      const saved = await postRow(sessionId, workspaceId, c)
      const note = draftNotes.current.get(c.requestId)
      draftNotes.current.delete(c.requestId)
      if (saved && note) await postNote(sessionId, workspaceId, note)
    })).then(() => {})
    flushing.current = { sessionId, done }
    return done
  }, [postRow])

  const load = useCallback(async (sessionId: string, workspaceId: string) => {
    const prev = sessionRef.current
    const nowIsDraft = isDraftId(sessionId)
    // Draft → real IS a session-id change, but it's the "compose a message with creds, then materialize into a real
    // session" handoff — creds carry over (flushDraft). Every other transition (real→draft when opening a new tab,
    // real→different real when switching tabs, draft→different draft) starts from an empty state so prior-session
    // rows don't leak into the popover.
    const handoff = (!prev || isDraftId(prev.sessionId)) && !nowIsDraft
    if (handoff) await flushDraft(sessionId, workspaceId)
    const isSameSession = sessionRef.current?.sessionId === sessionId
    if (!isSameSession) {
      setCredentials([])
      draftNotes.current.clear()
    }
    sessionRef.current = { sessionId, workspaceId }

    // Draft session — no server fetch.
    if (nowIsDraft) return

    try {
      const data = await request<Array<{
        requestId: string; file?: string; label?: string
        envMapping: Record<string, string>; entryId?: string; status: string
        provider?: string
        /** #1448 D2/D4: the row's one-time fields, as the SERVER knows them (the call's own card) */
        promptFields?: string[]
      }>>(`/api/agent/sessions/${sessionId}/credentials?workspaceId=${workspaceId}`)

      // Dedupe any server-side duplicates that accumulated before the
      // addPending fix. For each env var name we keep only the most
      // recently-persisted entry (last wins) and delete the earlier ones.
      const keepByEnv = new Map<string, typeof data[number]>()
      const toDrop = new Set<string>()
      for (const c of data) {
        // the key's variables only: two keys sharing a one-time variable don't collide (neither fills it, #1448 D4)
        const envNames = Object.values(keyFilledMapping(c.envMapping, c.promptFields ?? []))
        let replaced = false
        for (const env of envNames) {
          const prior = keepByEnv.get(env)
          if (prior && prior.requestId !== c.requestId) {
            toDrop.add(prior.requestId)
          }
          keepByEnv.set(env, c)
          replaced = true
        }
        if (!replaced) keepByEnv.set(c.requestId, c)
      }
      const kept = data.filter(c => !toDrop.has(c.requestId))
      if (toDrop.size > 0) {
        for (const id of toDrop) {
          request(`/api/agent/sessions/${sessionId}/credentials/${id}?workspaceId=${workspaceId}`, {
            method: 'DELETE',
          }).catch(() => {})
        }
      }

      // Merge: anything locally added but not yet visible on server is kept.
      // Only valid when reloading the SAME session — credsRef belongs to the
      // previous tab's session otherwise, and merging would leak its rows.
      const serverIds = new Set(kept.map(c => c.requestId))
      const localOnly = isSameSession
        ? credsRef.current.filter(c => !serverIds.has(c.requestId))
        : []
      setCredentials([
        ...kept.map(({ promptFields, ...c }) => {
          // #1448 D2: after a reload the call's input is gone — the server's one-time fields mark them, so the
          // session-credential dialog still leaves them out
          const local = isSameSession ? credsRef.current.find(l => l.requestId === c.requestId)?.fieldTypes : undefined
          const fromServer: Record<string, FieldType> = Object.fromEntries((promptFields ?? []).map(n => [n, 'prompt' as const]))
          const fieldTypes = local || promptFields?.length ? { ...local, ...fromServer } : undefined
          return { ...c, status: c.status as 'pending' | 'active', ...(fieldTypes ? { fieldTypes } : {}) }
        }),
        ...localOnly,
      ])
    } catch {
      // Keep current state on error
    }
  }, [flushDraft])

  const addPending = useCallback((cred: { requestId: string; file?: string; label?: string; envMapping: Record<string, string>; fieldTypes?: Record<string, FieldType>; provider?: string }) => {
    if (refusedIds.current.has(cred.requestId)) return
    forgetDrops(sessionRef.current?.sessionId, cred.requestId) // D52: synced again — a later drop is said again
    const known = credentials.find(c => c.requestId === cred.requestId)
    if (known) {
      // the SAME call seen again (a re-run after a resume, a newer SPA/worker): its mapping is what the tool told the
      // model on THIS run — the stored one may be an older run's (round 10). Take it; status and entry stay as they are.
      if (JSON.stringify(known.envMapping) === JSON.stringify(cred.envMapping)) return
      setCredentials(prev => prev.map(c => c.requestId === cred.requestId ? { ...c, envMapping: cred.envMapping, fieldTypes: cred.fieldTypes ?? c.fieldTypes } : c))
      syncToServer(cred.requestId, { envMapping: cred.envMapping })
      return
    }
    setCredentials(prev => {
      if (prev.some(c => c.requestId === cred.requestId)) return prev
      // replace stale creds matching by either:
      //   (a) env-var collision — any prior cred using the same env name
      //       (covers re-prompts where the AI passes the same env arg), OR
      //   (b) same-file unfilled pending — a prior cred_to_env call for
      //       this file that the user never resolved. The new call
      //       supersedes it; without this the old pending lingers for
      //       days in the popover.
      // …by the key's variables: a shared one-time variable is no collision (neither key fills it, #1448 D4)
      const keyVars = (c: { envMapping: Record<string, string>; fieldTypes?: Record<string, FieldType> }) =>
        Object.values(keyFilledMapping(c.envMapping, Object.keys(c.fieldTypes ?? {}).filter((n) => c.fieldTypes![n] === 'prompt')))
      const envKeys = new Set(keyVars(cred))
      const stale = prev.filter(c => {
        const envCollision = keyVars(c).some(v => envKeys.has(v))
        const staleSameFilePending = !!cred.file && c.file === cred.file && c.status === 'pending'
        return envCollision || staleSameFilePending
      })
      if (stale.length > 0) {
        const staleIds = new Set(stale.map(c => c.requestId))
        if (sessionRef.current) {
          const { sessionId, workspaceId } = sessionRef.current
          for (const id of staleIds) {
            request(`/api/agent/sessions/${sessionId}/credentials/${id}?workspaceId=${workspaceId}`, {
              method: 'DELETE',
            }).catch(() => {})
          }
        }
        return prev.filter(c => !staleIds.has(c.requestId))
          .concat([{ ...cred, status: 'pending' as const }])
      }
      return [...prev, { ...cred, status: 'pending' as const }]
    })
    syncToServer(cred.requestId, { ...cred, status: 'pending' })
  }, [credentials, syncToServer])

  // Each running cred_to_env call gets its pending row, under the mapping the worker told the model
  const activeToolCalls = opts.activeToolCalls
  useEffect(() => {
    if (!activeToolCalls) return
    for (const [callId, tc] of activeToolCalls) {
      if (tc.toolName !== 'cred_to_env') continue
      const input = tc.input as CredToEnvInput
      const envMapping = credToEnvMapping(input, callId)
      // Skip under-specified calls (no fields → empty envMapping): the worker returns retryable guidance for these
      // and never opens a form, so a pending row would be an orphan lingering in the keybook popover.
      if (Object.keys(envMapping).length === 0) continue
      // A call of only one-time (`prompt`) fields binds nothing (#1448 D4): its answer removes no row of a key, and a
      // row left under the keybook icon would open a form that answers nothing. The card is the only place it lives.
      const fieldList = input.fields || []
      if (fieldList.length > 0 && fieldList.every(f => f.type === 'prompt')) continue
      // Keybook off (owner 2026-10-22): the tool stays listed and REFUSES — it never opens a form either, so its
      // pending row would be the same orphan (and would surface the credentials popover on a keybook-off box).
      // The switch is flipped only while nobody is online (owner, 2026-10-22): a tab whose page-load reading is stale
      // is not a case to handle here — the server's 409 KEYBOOK_DISABLED is only a backstop.
      if (!keybookAvailable()) continue
      const fieldTypes: Record<string, FieldType> = {}
      for (const f of input.fields || []) fieldTypes[f.name] = f.type
      addPending({ requestId: callId, file: input.file, label: input.label, envMapping, fieldTypes, provider: input.provider })
    }
  }, [activeToolCalls, addPending])

  // D52: a turn of THIS session reported credentials the server dropped — tell the user (every reason), and take a
  // permanently dropped row off the popover now (the server already deleted it; a transient one stays, recoverable)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onDrops = (e: Event) => {
      const detail = (e as CustomEvent<CredentialDropsEventDetail>).detail
      if (!detail || detail.sessionId !== sessionRef.current?.sessionId) return
      const gone = new Set(detail.drops.filter(d => PERMANENT_CREDENTIAL_DROP_REASONS.has(d.reason)).map(d => d.requestId))
      if (gone.size > 0) setCredentials(prev => prev.filter(c => !gone.has(c.requestId)))
      if (announcedReports.has(detail)) return // another surface on this session took this report already
      announcedReports.add(detail)
      for (const d of takeNewDrops(detail.sessionId, detail.drops)) onDroppedRef.current?.(describeCredentialDrop(tRef.current as never, d))
    }
    window.addEventListener(CREDENTIAL_DROPS_EVENT, onDrops)
    return () => window.removeEventListener(CREDENTIAL_DROPS_EVENT, onDrops)
  }, [])

  /** Binds a pending session credential to its keybook entry and
   *  persists the change server-side. Returns a promise that resolves
   *  AFTER the session_credential row exists on the server — callers
   *  that fire a follow-up message (which the server resolves against
   *  that row) MUST await this, otherwise the message lands first and
   *  the env comes back empty in the worker. The label fetch keeps
   *  running in the background; awaiting bind() doesn't block on it. */
  const bind = useCallback(async (requestId: string, entryId: string, envMapping?: Record<string, string>, fieldMap?: Record<string, string>, fieldTypes?: Record<string, FieldType>): Promise<boolean> => {
    forgetDrops(sessionRef.current?.sessionId, requestId) // D52: a re-bound credential that drops again is said again
    // #1448 D4: a binding names every field of its call, one-time ones included — the SERVER knows which are one-time
    // (the call's own card, `session_credential.prompt_fields`) and never fills them from the key (`resolveByEntryId`)
    // `envMapping`: the mapping the card submitted — the one the tool told the model on this run (round 10); the
    // activation WRITES it, so a row stored by an older run (or an older SPA) never binds names the model doesn't use
    // #1324 S2: a card's bind — the person confirmed what the shared matcher filled — so it records 'kind' (a draft's
    // row keeps it for flushDraft, its first real write)
    setCredentials(prev => prev.some(c => c.requestId === requestId)
      ? prev.map(c => c.requestId === requestId ? { ...c, entryId, status: 'active' as const, fieldMatch: 'kind' as const, fieldMap, ...(envMapping ? { envMapping } : {}) } : c)
      : envMapping ? [...prev, { requestId, envMapping, entryId, status: 'active' as const, fieldMatch: 'kind' as const, ...(fieldMap ? { fieldMap } : {}), ...(fieldTypes ? { fieldTypes } : {}) }] : prev)
    // I198: true once the row is written. A draft has nothing server-side yet — flushDraft hands the row over before
    // the first message (the send path awaits it), so the local state IS the binding (true); syncToServer's own
    // false-for-a-draft means "not saved YET", which addFromKeybook's note relies on.
    const target = sessionRef.current
    const ok = !target || isDraftId(target.sessionId)
      ? true
      // #1441: the card's exact field map rides its own bind (a draft's row keeps it for flushDraft)
      : await syncToServer(requestId, { entryId, status: 'active', fieldMatch: 'kind', ...(fieldMap ? { fieldMap } : {}), ...(envMapping ? { envMapping } : {}) })
    // No label fetch here: GET /entries/:id decrypts the VALUE (and records the
    // person as having opened the key) — and its answer never carried `label`, so
    // the cosmetic label it looked for was never set. Callers that have the label
    // pass it (addFromKeybook's entryLabel).
    return ok
  }, [syncToServer])

  const hasEntry = useCallback((entryId: string) => {
    return credentials.some(c => c.entryId === entryId)
  }, [credentials])

  const addFromKeybook = useCallback((cred: { entryId: string; entryLabel: string; file?: string; envMapping: Record<string, string> }): Record<string, string> | null => {
    if (credentials.some(c => c.entryId === cred.entryId)) return null
    // Collect all env var names already in use and rename any collisions in
    // the incoming mapping by appending _2, _3, ... until unique.
    const usedNames = new Set<string>()
    for (const c of credentials) {
      for (const v of Object.values(c.envMapping)) usedNames.add(v)
    }
    const resolvedMapping: Record<string, string> = {}
    for (const [field, name] of Object.entries(cred.envMapping)) {
      // THE enforcement point for a keybook add: a VALID name is kept as is (`http_proxy` stays lower-case); only a
      // name the sandbox refuses is finalized, and so is a collision candidate that would be (a name at the 128-char
      // limit plus `_2`): finalizeEnvName cuts it with a hash of the WHOLE candidate, so `_2`, `_3`… stay distinct
      let candidate = usableEnvName(name)
      let n = 2
      while (usedNames.has(candidate)) {
        candidate = usableEnvName(`${name}_${n}`)
        n++
      }
      resolvedMapping[field] = candidate
      usedNames.add(candidate)
    }

    const requestId = `manual_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    const newCred: CredRequest = {
      requestId,
      file: cred.file,
      label: cred.entryLabel,
      entryLabel: cred.entryLabel,
      envMapping: resolvedMapping,
      entryId: cred.entryId,
      status: 'active',
    }
    setCredentials(prev => [...prev, newCred])
    // the model is told only once the row is SAVED (rounds 9–11): now, or — for a draft — when flushDraft lands it
    const note = manualCredentialNote(cred.entryLabel, resolvedMapping)
    const target = sessionRef.current
    if (!target || isDraftId(target.sessionId)) {
      draftNotes.current.set(requestId, note)
    } else {
      void syncToServer(requestId, newCred).then(saved => { if (saved) void postNote(target.sessionId, target.workspaceId, note) })
    }
    return resolvedMapping
  }, [credentials, syncToServer])

  const remove = useCallback((requestId: string, reason: 'user' | 'keybook-deleted' | 'one-time' = 'user') => {
    draftNotes.current.delete(requestId)
    const cred = credentials.find(c => c.requestId === requestId)
    setCredentials(prev => prev.filter(c => c.requestId !== requestId))
    // Delete from server
    if (sessionRef.current) {
      const { sessionId, workspaceId } = sessionRef.current
      request(`/api/agent/sessions/${sessionId}/credentials/${requestId}?workspaceId=${workspaceId}`, {
        method: 'DELETE',
      }).catch(() => {})
      // Insert a system message so the AI knows the env vars are gone.
      // Phrasing differs by cause so the AI can pick the right follow-up:
      //   user: they explicitly removed it → suggest cred_to_env
      //   keybook-deleted: backing entry was deleted externally → same
      //     advice, but the AI should know it wasn't the user's action
      //     inside this session
      // #1448: a card answered with one-time codes alone binds nothing — its pending row goes quietly (the codes reach
      // that turn through the answer; nothing was removed)
      if (cred && reason !== 'one-time') {
        // Dedupe — hidden providers alias every field to the same
        // internal env var, so a naive list prints duplicates.
        const envVars = Array.from(new Set(Object.values(cred.envMapping))).map(v => `$${v}`).join(', ')
        // These messages are PERSISTED into the transcript. With the keybook
        // off the cred_to_env hint is omitted (the call would only be refused —
        // the tool stays listed and explains itself). Same rule, and same
        // shape, as the server-side drop messages in credential-resolver.ts;
        // the synchronous reader exists for exactly this non-React call site.
        // The FACT half of each message is unchanged either way.
        const credHint = keybookAvailable()
          ? (reason === 'keybook-deleted'
              ? ' If you need this credential again, call cred_to_env to re-prompt the user.'
              : ' If you need these credentials again, call cred_to_env.')
          : ''
        const content = reason === 'keybook-deleted'
          ? `The keybook entry backing ${envVars} was deleted (from Keybook view or another session). These environment variables are NO LONGER available for this session. Do not use them — they will fail.${credHint}`
          : `Credential removed by user. The following environment variables are NO LONGER available: ${envVars}. Do not use them — they will fail.${credHint}`
        request(`/api/agent/sessions/${sessionId}/system-message?workspaceId=${workspaceId}`, {
          method: 'POST',
          body: JSON.stringify({ content }),
        }).catch(() => {})
      }
    }
  }, [credentials])

  const resolveCard = useCallback(async (callId: string, envMapping: Record<string, string>, entryId?: string, fieldMap?: Record<string, string>, opts?: { oneTimeOnly?: boolean; fieldTypes?: Record<string, FieldType> }): Promise<boolean> => {
    // entryId must be present — the form always saves to the keybook. Without it the per-turn resolver has nothing
    // to look values up from, so the row is dropped instead of becoming an active-but-unresolvable cred.
    // RETURNED (I198 Codex round): the card awaits the binding before it replies, and reports whether it landed.
    if (entryId) return bind(callId, entryId, envMapping, fieldMap, opts?.fieldTypes)
    // Codes alone say nothing about a key: only a PENDING row goes, an active one keeps its binding (a re-asked card)
    const row = credentials.find(c => c.requestId === callId)
    if (row && (!opts?.oneTimeOnly || row.status === 'pending')) remove(callId, opts?.oneTimeOnly ? 'one-time' : 'user')
    return false
  }, [bind, credentials, remove])

  const clear = useCallback(() => {
    setCredentials([])
    draftNotes.current.clear()
  }, [])

  return {
    credentials,
    addPending,
    bind,
    resolveCard,
    addFromKeybook,
    hasEntry,
    remove,
    clear,
    load,
    flushDraft,
  }
}
